import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { FILES } from "@/constants/contentTestIds";

/**
 * Generic asset uploader. Stores via /api/files/upload (local-disk M2) and
 * yields the public URL. Supports image and PDF previews (extensible).
 *
 * Props:
 *   value:        current URL or ""
 *   onChange:     (url|"") => void
 *   brandId:      brand the asset belongs to
 *   accept:       file picker filter (default "image/*"). Use
 *                 "image/*,application/pdf" to allow both.
 *   label:        button label override (default derived from accept)
 *   hint:         hint text under the button (default derived from accept)
 *   testIdSuffix: appended to data-testids
 */
export default function AssetUpload({
  value,
  onChange,
  brandId,
  accept = "image/*",
  label,
  hint,
  testIdSuffix = "",
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const isPdf = typeof value === "string" && /\.pdf(\?|#|$)/i.test(value);
  const acceptsPdf = accept.includes("pdf");

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/files/upload?brand_id=${brandId}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const buttonLabel = label || (acceptsPdf ? "Upload image or PDF" : "Upload image");
  const hintLabel = hint || (acceptsPdf ? "JPG, PNG, SVG or PDF." : "JPG, PNG, etc.");

  return (
    <div className="space-y-2">
      {value ? (
        <div className="flex items-start gap-3">
          {isPdf ? (
            <div
              className="w-24 h-24 border border-[#1A1F2C]/15 rounded-md bg-[#F8F5EE] flex flex-col items-center justify-center text-center px-2"
              data-testid={`${FILES.preview || "file-preview-"}pdf${testIdSuffix}`}
            >
              <span className="text-xs uppercase tracking-[0.18em] text-[#B5462F] font-medium">PDF</span>
              <a
                href={value}
                target="_blank"
                rel="noreferrer"
                className="text-[10px] text-[#1A1F2C] underline mt-1 break-all"
              >
                Open
              </a>
            </div>
          ) : (
            <img
              src={value}
              alt=""
              className="w-24 h-24 object-cover border border-[#1A1F2C]/15 rounded-md bg-[#F8F5EE]"
            />
          )}
          <div className="flex flex-col gap-2 text-xs">
            <code className="text-[10px] text-[#6B6358] break-all">{value}</code>
            <button
              data-testid={`${FILES.clearButton}${testIdSuffix}`}
              type="button"
              onClick={() => onChange("")}
              className="self-start px-3 py-1 text-[#B5462F] border border-[#B5462F]/40 rounded-full"
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            data-testid={`${FILES.upload}${testIdSuffix}`}
            onChange={onPick}
            className="hidden"
            id={`fileinput-${testIdSuffix}`}
          />
          <label
            htmlFor={`fileinput-${testIdSuffix}`}
            data-testid={`${FILES.uploadButton}${testIdSuffix}`}
            className="px-4 py-2 border border-[#1A1F2C]/20 rounded-full text-sm cursor-pointer hover:border-[#1A1F2C]/40"
          >
            {uploading ? "Uploading..." : buttonLabel}
          </label>
          <span className="text-xs text-[#6B6358]">{hintLabel}</span>
        </div>
      )}
      {error && <p className="text-xs text-[#B5462F]">{error}</p>}
    </div>
  );
}
